import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Lock, KeyRound, ClipboardList, RefreshCw, Send, User, Shield, Bell,
  Clock, Loader2, CheckCircle2, XCircle,
} from 'lucide-react';
import { useLang } from '@/context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending:     { id: 'Menunggu',       en: 'Pending',     color: '#F59E0B', Icon: Clock },
  in_progress: { id: 'Sedang Diproses', en: 'In Progress', color: '#2563EB', Icon: Loader2 },
  done:        { id: 'Selesai',        en: 'Done',        color: '#16A34A', Icon: CheckCircle2 },
  rejected:    { id: 'Ditolak',        en: 'Rejected',    color: '#DC2626', Icon: XCircle },
};

const fmtDT = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function TicketTracker() {
  const { lang } = useLang();
  const isID = lang !== 'en';

  // Passkey gate — session only, mirrors TicketSupport
  const [ticketUnlocked, setTicketUnlocked] = useState(() => {
    try { return sessionStorage.getItem('mg_ticket_unlocked') === '1'; }
    catch { return false; }
  });
  const [unlockedWA, setUnlockedWA] = useState(() => {
    try { return sessionStorage.getItem('mg_ticket_wa') || ''; }
    catch { return ''; }
  });
  const [unlockWA, setUnlockWA] = useState('');
  const [unlockPasskey, setUnlockPasskey] = useState('');
  const [unlockLoading, setUnlockLoading] = useState(false);

  const [ticketId, setTicketId] = useState('');
  const [wa, setWa] = useState('');
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const unlockTicket = async () => {
    if (!unlockWA.trim() || !unlockPasskey.trim()) {
      return toast.error(isID ? 'Isi WhatsApp & Passkey' : 'Fill WhatsApp & Passkey');
    }
    setUnlockLoading(true);
    try {
      await axios.post(`${API}/auth/login`, {
        whatsapp: unlockWA.trim(),
        passkey: unlockPasskey.trim(),
      });
      const w = unlockWA.trim();
      setTicketUnlocked(true);
      setUnlockedWA(w);
      setWa(w);
      try {
        sessionStorage.setItem('mg_ticket_unlocked', '1');
        sessionStorage.setItem('mg_ticket_wa', w);
      } catch { /* noop */ }
      setUnlockPasskey('');
      toast.success(isID ? 'Berhasil dibuka' : 'Unlocked');
    } catch (err) {
      toast.error(err.response?.data?.detail || (isID ? 'Passkey salah' : 'Invalid passkey'));
    }
    setUnlockLoading(false);
  };

  const track = useCallback(async () => {
    if (!ticketId.trim() || !wa.trim()) {
      return toast.error(isID ? 'Isi ID Tiket & WhatsApp' : 'Fill Ticket ID & WhatsApp');
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/tickets/track`, {
        ticket_id: ticketId.trim(),
        reporter_whatsapp: wa.trim(),
      });
      setTicket(data.ticket);
    } catch (err) {
      setTicket(null);
      toast.error(err.response?.data?.detail || (isID ? 'Gagal memuat tiket' : 'Failed to load ticket'));
    }
    setLoading(false);
  }, [ticketId, wa, isID]);

  const sendReply = async () => {
    if (!reply.trim() || !ticket) return;
    setSending(true);
    try {
      await axios.post(`${API}/tickets/reply`, {
        ticket_id: ticket.id,
        reporter_whatsapp: wa.trim(),
        text: reply.trim(),
      });
      setReply('');
      await track();
      toast.success(isID ? 'Pesan terkirim' : 'Message sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || (isID ? 'Gagal kirim' : 'Failed to send'));
    }
    setSending(false);
  };

  // PASSKEY GATE
  if (!ticketUnlocked) {
    return (
      <section className="py-10 sm:py-14 px-4 sm:px-6" data-testid="ticket-tracker">
        <div className="max-w-3xl mx-auto">
          <div className="glass-card rounded-3xl p-6 sm:p-8" data-testid="ticket-tracker-gate">
            <div className="text-center mb-5">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-3" style={{ background: 'rgba(217,138,148,0.12)' }}>
                <Lock className="w-6 h-6 text-[#D98A94]" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold t-text mb-1">
                {isID ? 'Passkey wajib untuk cek tiket' : 'Passkey required to check ticket'}
              </h3>
              <p className="t-text-secondary text-xs sm:text-sm max-w-md mx-auto">
                {isID
                  ? 'Masukkan nomor WhatsApp dan passkey bulanan yang diberikan admin.'
                  : 'Enter your WhatsApp and the monthly passkey given by admin.'}
              </p>
            </div>
            <div className="max-w-md mx-auto space-y-3">
              <Input placeholder={isID ? 'WhatsApp (mis. +6281234567890)' : 'WhatsApp (e.g. +6281234567890)'}
                value={unlockWA} onChange={e => setUnlockWA(e.target.value)}
                data-testid="ticket-tracker-unlock-wa" autoComplete="off" />
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 t-text-muted" />
                <Input type="password" placeholder={isID ? 'Kode Passkey' : 'Passkey'}
                  value={unlockPasskey} onChange={e => setUnlockPasskey(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') unlockTicket(); }}
                  className="pl-9" data-testid="ticket-tracker-unlock-passkey" autoComplete="off" />
              </div>
              <Button onClick={unlockTicket} disabled={unlockLoading} data-testid="ticket-tracker-unlock-btn"
                className="w-full bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full h-11 font-semibold">
                {unlockLoading ? (isID ? 'Memverifikasi...' : 'Verifying...') : (isID ? 'Buka Cek Tiket' : 'Unlock Tracker')}
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // UNLOCKED — track form + result
  return (
    <section className="py-10 sm:py-14 px-4 sm:px-6" data-testid="ticket-tracker">
      <div className="max-w-3xl mx-auto space-y-4">
        <div className="glass-card rounded-3xl p-5 sm:p-6">
          <div className="flex items-center gap-2 mb-3">
            <ClipboardList className="w-4 h-4 text-[#D98A94]" />
            <h3 className="font-bold t-text">{isID ? 'Cek Status Tiket' : 'Track Your Ticket'}</h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input placeholder={isID ? 'ID Tiket (dari email/notifikasi)' : 'Ticket ID (from confirmation)'}
              value={ticketId} onChange={e => setTicketId(e.target.value)} data-testid="ticket-tracker-id" />
            <Input placeholder="WhatsApp *" value={wa} onChange={e => setWa(e.target.value)} data-testid="ticket-tracker-wa" />
          </div>
          <Button onClick={track} disabled={loading} data-testid="ticket-tracker-submit"
            className="mt-3 bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
              <><RefreshCw className="w-3.5 h-3.5 mr-1.5" />{isID ? 'Cek Sekarang' : 'Check Now'}</>
            )}
          </Button>
          <p className="text-[11px] t-text-muted mt-2">
            {isID ? 'Terbuka untuk' : 'Unlocked as'} <span className="font-semibold t-text">{unlockedWA}</span>
          </p>
        </div>

        {ticket && <TicketDetail ticket={ticket} lang={lang} onReply={sendReply} reply={reply} setReply={setReply} sending={sending} />}
      </div>
    </section>
  );
}

function TicketDetail({ ticket, lang, onReply, reply, setReply, sending }) {
  const isID = lang !== 'en';
  const meta = STATUS_META[ticket.status] || STATUS_META.pending;
  const StatusIcon = meta.Icon;
  return (
    <>
      <div className="glass-card rounded-3xl p-5 sm:p-6" data-testid="ticket-tracker-detail">
        <div className="flex items-center gap-3 flex-wrap mb-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: `${meta.color}22`, color: meta.color }}>
            <StatusIcon className={`w-5 h-5 ${ticket.status === 'in_progress' ? 'animate-spin' : ''}`} />
          </div>
          <div>
            <div className="text-xs t-text-muted">{isID ? 'Status Saat Ini' : 'Current Status'}</div>
            <div className="text-lg font-bold" style={{ color: meta.color }}>{meta[isID ? 'id' : 'en']}</div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-[10px] t-text-muted uppercase tracking-wide">{isID ? 'Terakhir diperbarui' : 'Last update'}</div>
            <div className="text-xs t-text">{fmtDT(ticket.updated_at)}</div>
          </div>
        </div>
        {ticket.admin_note && (
          <div className="rounded-lg p-3 text-sm border" style={{ background: `${meta.color}0F`, borderColor: `${meta.color}55` }}>
            <div className="text-[10px] font-bold uppercase tracking-wide mb-1" style={{ color: meta.color }}>
              {isID ? 'Catatan Admin' : 'Admin Note'}
            </div>
            <p className="t-text">{ticket.admin_note}</p>
          </div>
        )}
      </div>

      {/* Thread */}
      <div className="glass-card rounded-3xl p-5 sm:p-6">
        <div className="font-semibold t-text mb-3 flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#D98A94]" />
          {isID ? 'Percakapan' : 'Conversation'}
        </div>
        <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1" data-testid="ticket-tracker-messages">
          {(ticket.messages || []).length === 0 ? (
            <p className="text-xs t-text-muted italic">{isID ? 'Belum ada pesan.' : 'No messages yet.'}</p>
          ) : (
            ticket.messages.map((m) => <Bubble key={m.at + m.sender} msg={m} isID={isID} />)
          )}
        </div>
        <div className="mt-3">
          {ticket.status === 'done' || ticket.status === 'rejected' ? (
            <div className="rounded-lg p-3 text-center text-xs" style={{ background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.30)', color: '#16A34A' }} data-testid="ticket-tracker-closed-banner">
              {isID
                ? '✓ Tiket sudah ditandai selesai oleh admin. Chat sudah ditutup.'
                : '✓ Ticket marked resolved by admin. Chat is closed.'}
            </div>
          ) : (
            <div className="flex items-end gap-2">
              <Textarea value={reply} onChange={e => setReply(e.target.value)}
                placeholder={isID ? 'Balasan Anda ke admin...' : 'Your reply to admin...'}
                className="min-h-[64px] flex-1" data-testid="ticket-tracker-reply-input" />
              <Button onClick={onReply} disabled={sending || !reply.trim()} data-testid="ticket-tracker-reply-send"
                className="bg-[#D98A94] hover:bg-[#D98A94]/90 text-white rounded-full h-10 self-center">
                <Send className="w-3.5 h-3.5 mr-1" />{isID ? 'Kirim' : 'Send'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Bubble({ msg, isID }) {
  const isAdmin = msg.sender === 'admin';
  const isReporter = msg.sender === 'reporter';
  const isSystem = msg.sender === 'system';
  const senderLabel = isAdmin ? 'Admin' : isReporter ? (isID ? 'Anda' : 'You') : (isID ? 'Sistem' : 'System');
  const SenderIcon = isAdmin ? Shield : isReporter ? User : Bell;
  const API_ROOT = process.env.REACT_APP_BACKEND_URL || '';
  const mediaUrl = (p) => `${API_ROOT}/api/tickets/media/${p}`;
  return (
    <div className={`flex ${isReporter ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${isReporter ? 'bg-[#D98A94] text-white' : isSystem ? 'bg-amber-50 border border-amber-200 text-amber-900' : 'glass-card t-text'}`}>
        <div className="flex items-center gap-1 text-[10px] font-semibold mb-0.5 opacity-80 uppercase tracking-wide">
          <SenderIcon className="w-3 h-3" />
          {senderLabel} · {fmtDT(msg.at)}
        </div>
        {msg.text && <div className="whitespace-pre-wrap break-words">{msg.text}</div>}
        {(msg.photos || []).length > 0 && (
          <div className={`grid gap-1.5 mt-1.5 ${msg.photos.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
            {msg.photos.map((p, i) => (
              <a key={p} href={mediaUrl(p)} target="_blank" rel="noreferrer" data-testid={`tracker-photo-${i}`}
                className="block rounded-lg overflow-hidden bg-black/10 aspect-square max-h-56">
                <img src={mediaUrl(p)} alt="photo" className="w-full h-full object-cover" />
              </a>
            ))}
          </div>
        )}
        {(msg.videos || []).length > 0 && (
          <div className="space-y-1.5 mt-1.5">
            {msg.videos.map((v, i) => (
              <video key={v} src={mediaUrl(v)} controls preload="metadata" playsInline
                data-testid={`tracker-video-${i}`}
                className="w-full max-w-md rounded-lg bg-black" />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
